/**
 * How far behind the newest footage live playback should sit, and which clip
 * that lands on.
 *
 * Live-follow re-anchors to the NEWEST clip on every rollover, which sounds like
 * "as live as possible" and behaves like the opposite. Footage arrives 17–68 s
 * after a clip ends (measured across four cameras on 2026-08-05, varying by site
 * AND by hour), so a player pinned to the newest clip runs out of material
 * before the next one lands. Replaying real clip arrivals: pinning to the newest
 * clip plays 34% of the time with 35 stalls in 28 minutes. Sitting ~90–120 s
 * back plays without stalling at all.
 *
 * That trade is invisible to the operator — 95 s versus 60 s behind, on a
 * pipeline already a minute behind — and it is the difference between continuous
 * playback and a picture that keeps freezing and jumping.
 *
 * These are pure so the policy is testable: the failure they fix takes minutes
 * of real footage to reproduce in a browser.
 */

/**
 * Never sit closer than this to the edge — below it, ordinary jitter stalls
 * playback.
 *
 * 120s, not 60s. The floor governs far more often than it looks: `liveBufferMs`
 * needs 3 arrivals before it adapts, and `getClipArrivals` only counts clips
 * that STARTED after the operator opened the page — so with 30s clips the
 * adaptive path cannot engage for the first ~90 seconds, and every fresh load
 * runs on this constant.
 *
 * At 60s that floor sat BELOW the pipeline's own worst case: footage becomes
 * fetchable 17–68s after a clip ENDS, so playback 60s behind the newest clip can
 * reach material that has not been uploaded yet and starve. Measured on Casa de
 * Pedra with the 60s floor: both tiles played 55–61% of real time with ~40% of
 * samples stalled. 120s clears the measured lag with a clip to spare, and
 * matches the replay result that ~90–120s back plays stall-free.
 *
 * The cost is latency the operator cannot perceive — 2 minutes versus 1 on a
 * pipeline already a minute behind — bought against a picture that keeps
 * freezing, which they can.
 */
export const MIN_LIVE_BUFFER_MS = 120_000;
/** Never sit further back than this, however bad the pipeline looks. */
export const MAX_LIVE_BUFFER_MS = 180_000;
/** Headroom added to the observed lag, roughly one clip. */
export const LIVE_BUFFER_MARGIN_MS = 30_000;
/** Arrivals older than this say nothing about the pipeline's current behaviour. */
const SAMPLE_WINDOW = 20;

export interface ClipArrival {
  /** `start_time` of the clip, in ms. */
  startMs: number;
  /** When the client first saw it — fetch time, not upload time. */
  visibleAtMs: number;
}

const percentile = (sorted: number[], p: number): number =>
  sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))];

/**
 * The buffer to keep behind the newest footage, from what this camera has
 * actually been doing.
 *
 * Uses `visibleAt − start` (not `− end`): playback reaches a clip at its START,
 * so that is the deadline the clip has to beat. p90 rather than the max, with a
 * clip of margin — the max is a single worst case and would park playback
 * needlessly far back for the other 90% of the shift.
 *
 * Falls back to the minimum when there is nothing to go on, which is also the
 * right answer for a camera that has just come online.
 */
export const liveBufferMs = (
  arrivals: readonly ClipArrival[],
  /**
   * Floor for the buffer. Defaults to the measured stall-free minimum; the map
   * passes the operator's chosen delay so how far behind live sits is their
   * call, not a constant. Still only a FLOOR — a camera whose own arrivals have
   * been slower than this widens past it, and `MAX_LIVE_BUFFER_MS` still caps it.
   */
  floorMs: number = MIN_LIVE_BUFFER_MS,
): number => {
  const recent = arrivals.slice(-SAMPLE_WINDOW);
  if (recent.length < 3) return floorMs;

  const lags = recent.map((a) => a.visibleAtMs - a.startMs).sort((a, b) => a - b);
  const target = percentile(lags, 0.9) + LIVE_BUFFER_MARGIN_MS;
  return Math.min(MAX_LIVE_BUFFER_MS, Math.max(floorMs, target));
};

/**
 * Where the live cursor should sit across the view, given the buffer.
 *
 * `LIVE_CURSOR_ANCHOR` is a fixed 0.9 — the playhead one minute from the right
 * of a 10-minute view. With a 90–120 s buffer the cursor is further left than
 * its own anchor, so `useLiveTimelineDomain` gives up anchoring and follows the
 * wall clock instead, and the view drifts. Deriving the anchor from the buffer
 * keeps the two agreeing at any span or buffer.
 *
 * Clamped: a buffer approaching the span would push the anchor off the left of
 * the view, and an anchor past 0.95 leaves no room for the events that lead
 * footage.
 */
export const liveAnchorFraction = (bufferMs: number, spanMs: number): number => {
  if (!(spanMs > 0)) return 0.9;
  return Math.min(0.95, Math.max(0.5, 1 - bufferMs / spanMs));
};

export interface ClipLike {
  start_time: string;
  end_time: string;
}

/**
 * The clip live playback should be on: the one covering `bufferMs` behind the
 * newest loaded clip's end.
 *
 * Returns the newest clip when the accumulation is shorter than the buffer
 * (early in a session there is nothing older to sit on), and null when there is
 * nothing loaded at all. Never returns a clip the caller does not have — the
 * index is into the list it passed.
 */
export const liveClipIndex = (clips: readonly ClipLike[], bufferMs: number): number | null => {
  if (clips.length === 0) return null;

  const endMs = new Date(clips[clips.length - 1].end_time).getTime();
  if (!Number.isFinite(endMs)) return clips.length - 1;

  const target = endMs - bufferMs;
  // Walk back from the newest: the target is near the end by construction, and
  // the list is sorted by start.
  for (let i = clips.length - 1; i >= 0; i--) {
    const start = new Date(clips[i].start_time).getTime();
    if (start <= target) return i;
  }
  // Everything loaded is newer than the target — sit on the oldest we hold and
  // let playback carry forward rather than jumping to the edge.
  return 0;
};

/**
 * The clip live-follow should roll to when the current one ends.
 *
 * Behind the buffered position: re-anchor forward to it — that is the drift cap
 * live-follow exists for. At or ahead of it: step ONE clip.
 *
 * Never the newest clip. Targeting `clips.length - 1` here defeats the buffer at
 * exactly the moment it starts working: measured live, playback that had just
 * caught up (current = buffered = 13, newest = 15) was sent to 15, skipping a
 * clip and landing on the live edge — a visible jump, and then a stall, because
 * the edge is where the next file has not been uploaded yet.
 */
export const nextLiveClipIndex = (currentIndex: number, buffered: number | null): number =>
  buffered != null && buffered > currentIndex ? buffered : currentIndex + 1;

/**
 * Whether playback has fallen far enough behind its target to be worth a jump.
 *
 * A jump is visible, so it has to be worth seeing: this is the difference
 * between "drifting a clip behind" (let playback catch up on its own) and
 * "stuck minutes behind footage we already hold" (the bug this exists for).
 */
export const shouldRealignToBuffer = (
  currentIndex: number,
  targetIndex: number,
  clips: readonly ClipLike[],
  toleranceMs: number,
): boolean => {
  if (targetIndex <= currentIndex) return false;
  const current = clips[currentIndex];
  const target = clips[targetIndex];
  if (!current || !target) return false;
  const currentStart = new Date(current.start_time).getTime();
  const targetStart = new Date(target.start_time).getTime();
  if (!Number.isFinite(currentStart) || !Number.isFinite(targetStart)) return false;
  return targetStart - currentStart > toleranceMs;
};
